import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { SortDirection } from 'react-virtualized';
import styled from 'styled-components';
import { ReactComponent as MenuIcon } from '~/shared/assets/three_dots.svg';
import TableSearchBox from '../Search/TableSearchBox';
import useDebounce from '../../hooks/useDebounce';
import {
  buildTableExportColumnsFromRowKeys,
  buildTableExportSheet,
  downloadTableExportCsv,
  type TableExportColumn,
} from './tableExport';
import { utils, writeFileXLSX } from 'xlsx';
import BaseTable from './baseTable';

const Wrapper = styled.div<{ $gridheight: number }>`
  height: ${({ $gridheight }) => (!$gridheight ? '600px' : `${Math.floor($gridheight) * 102}px`)};
  background-color: var(--table-wrapper-background-color);
  border-radius: 4px;
  padding: 15px 0 25px 0;
  width: 100%;
  max-width: 100%;
  margin-top: 10px;
`;

const Header = styled.div<{ height: number }>`
  display: flex;
  flex-direction: column;
  align-items: baseline;
  height: ${({ height }) => `${height + 10}px`};
  color: #4c5e72;
  font-size: 18px;
  font-weight: 500;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

const SubHeader = styled.div<{ $titlerows: number }>`
  display: flex;
  flex-direction: column;
  align-items: baseline;
  height: 36px;
  color: #4c5e72;
  margin-top: ${({ $titlerows }) => ($titlerows === 2 ? 8 : -10)}px;
  font-size: 14px;
  font-weight: 500;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

const TextArea = styled.textarea`
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  font-size: 18px;
  font-weight: 500;
  color: #4c5e72;
  font-family: Montserrat, sans-serif;
  line-height: 20px;
  padding: 0px 0px 0px 10px;
  margin: 0px;
`;

const SubtitleTextArea = styled.textarea`
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  font-size: 14px;
  font-weight: 500;
  color: #4c5e72;
  font-family: Montserrat, sans-serif;
  line-height: 14px;
  padding: 0px 0px 0px 10px;
  margin: 0;
`;

const HeaderWrapper = styled.div`
  min-height: 70px;
  margin-top: -18px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 90%;
  margin-top: 18px;
`;

const RightWrapper = styled.div<{ $issearchvisible: number }>`
  width: ${({ $issearchvisible }) => ($issearchvisible ? '300px' : '16px')};
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
`;

const MenuIconWrapper = styled.div`
  cursor: pointer;
  margin-right: -14px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  fill: #4c5e72;
  stroke: transparent;
  &:hover {
    background: #d4e5ff;
  }
`;

const SearchWrapper = styled.div`
  cursor: pointer;
  height: 32px;
  margin-right: 8px;
  margin-top: 3px;
`;

const MenuOptions = styled.div`
  width: 92%;
  max-width: 360px;
  height: 110px;
  cursor: pointer;
  z-index: 1000 !important;
  background-color: var(--table-menu-options-background-color);
  opacity: 1;
  margin: -10px 3% 0 auto;
  direction: ltr;
  position: absolute;
  font-size: 12px;
  top: 66px;
  right: -38px;
  list-style: none;
  border: 1px solid var(--table-menu-options-border-color);
`;

const Options = styled.ul`
  list-style-type: none;
  padding: 0px;
  font-family: Montserrat, sans-serif;
`;

const Items = styled.li`
  &:hover {
    background-color: #d4e5ff;
  }

  cursor: pointer;
  color: var(--table-item-color);
  background: none;
  font-size: 16px;
  transition:
    background 250ms ease 0s,
    color 250ms ease 0s;
  font-family: Montserrat, sans-serif;
  line-height: 20px;
  padding: 9px 5px 5px 12px;
`;

export type TableProps = {
  title: string;
  subtitle?: string;
  data: Record<string, unknown>[];
  dataKeys: string[];
  dataKeysDescription?: string[];
  dataKeysColors?: any[];
  dataKeysWidth?: any[];
  dataTypes?: (string | undefined)[];
  dataAlignments?: (string | undefined)[];
  dataMidPoints?: number[];
  dataColors?: string[];
  isLoading: boolean;
  headerHeight?: number;
  disableSort?: boolean;
  rowHeight?: number;
  emptyStateText?: string;
  numberSortedKeys?: string[];
  onRowClick?: (data: any) => void;
  onHeaderClick?: (data: any) => void;
  gridHeight?: any;
  size?: any;
  defaultSortBy?: string;
  hasRowNodes?: boolean;
  headersDataKeys?: any;
  headersDataKeysDescription?: any;
  headersDataKeysWidth?: any;
  headersDataTypes?: any;
  omitHeaders?: any;
  firstItems?: string[];
  lastItems?: string[];
  headerFirstItems?: string[];
  headerLastItems?: string[];
  isDoubleHeaderHeight?: boolean;
  isDoubleSubHeaderHeight?: boolean;
  hasHeaders?: boolean;
  indexTitle?: string;
  indexTitleWidth?: number;
  columnIds?: string[];
  headerColumnIds?: string[];
  exportColumns?: TableExportColumn[];
  includeExportParentHeaderRow?: boolean;
};

const extractComparableValue = (value: unknown): any => {
  if (value && typeof value === 'object' && 'text' in (value as any)) {
    return (value as any).text;
  }
  return value;
};

const sortDataByCriteria = (data: any[], criteria: any, sortDirection: any, numberSortedKeys: string[] | undefined) =>
  data?.sort((a: any, b: any) => {
    const rawA = extractComparableValue(a[criteria]);
    const rawB = extractComparableValue(b[criteria]);

    let checkA: any;
    let checkB: any;
    if (String(rawA).includes(',')) {
      checkA = Math.abs(Number(String(rawA).split(',')[0])) + Math.abs(Number(String(rawA).split(',')[1]));
      checkB = Math.abs(Number(String(rawB).split(',')[0])) + Math.abs(Number(String(rawB).split(',')[1]));
    } else if (
      (numberSortedKeys && numberSortedKeys.includes(criteria.replace('_', ' '))) ||
      typeof rawA !== 'string'
    ) {
      checkA = Number(String(rawA).replace('%', ''));
      checkB = Number(String(rawB).replace('%', ''));
    } else {
      checkA = String(rawA).toLowerCase();
      checkB = String(rawB).toLowerCase();
    }
    if (sortDirection === 'ASC' ? checkA > checkB : checkA < checkB) {
      return 1;
    }
    return -1;
  });

export default function Table({
  title,
  subtitle,
  data,
  dataKeys,
  dataKeysDescription = [],
  dataKeysColors = [],
  dataKeysWidth = undefined,
  dataTypes = [],
  dataAlignments = [],
  dataMidPoints = [],
  dataColors = [],
  isLoading,
  headerHeight = 35,
  rowHeight = 50,
  emptyStateText = 'No Data',
  disableSort,
  numberSortedKeys = ['order'],
  onRowClick,
  onHeaderClick,
  gridHeight,
  defaultSortBy = 'order',
  size,
  hasRowNodes = false,
  headersDataKeys,
  headersDataKeysDescription,
  headersDataKeysWidth,
  headersDataTypes,
  omitHeaders,
  firstItems = [],
  lastItems = [],
  headerFirstItems = [],
  headerLastItems = [],
  isDoubleHeaderHeight,
  isDoubleSubHeaderHeight,
  hasHeaders,
  indexTitle,
  indexTitleWidth,
  columnIds = [],
  headerColumnIds = [],
  exportColumns,
  includeExportParentHeaderRow = false,
}: TableProps) {
  const defaultSortDirection = SortDirection.ASC;

  const [openMenu, setOpenMenu] = useState(false);

  const MenuOptionsData = [
    { name: 'View in full screen', id: 1 },
    { name: 'Download XLS', id: 2 },
    { name: 'Download CSV', id: 3 },
  ];

  const [sort, setSort] = useState({
    sortBy: defaultSortBy,
    sortDirection: defaultSortDirection,
  });

  const { sortBy, sortDirection } = sort;

  const sortedItems = useMemo(() => {
    return disableSort ? data : sortDataByCriteria(data, sortBy, sortDirection, numberSortedKeys);
  }, [data, sort, sortBy, sortDirection, disableSort, numberSortedKeys]);

  const isSearchVisible = useMemo(() => sortedItems.length > 10, [sortedItems]);

  const handleSetSort = useCallback(
    (props: any) => {
      const { sortBy } = props as unknown as any;
      const sortObj =
        sortBy !== sort.sortBy && sortBy !== ''
          ? { sortBy, sortDirection: SortDirection.ASC }
          : sort.sortDirection === SortDirection.ASC
            ? { sortBy, sortDirection: SortDirection.DESC }
            : { sortBy: defaultSortBy, sortDirection: SortDirection.ASC };
      setSort({ ...props, sortBy: sortObj.sortBy, sortDirection: sortObj.sortDirection });
    },
    [setSort, sort, defaultSortBy],
  );

  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue, 200);

  const handleMenuClick = useCallback(() => {
    setOpenMenu(!openMenu);
  }, [openMenu]);

  const ref: any = useRef();
  const elem: any = ref.current;

  const makeFullscreen = useCallback(() => {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      /* Firefox */
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      /* Chrome, Safari and Opera */
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      /* IE/Edge */
      elem.msRequestFullscreen();
    }
  }, [elem]);

  const handleSearchChange = (event: any) => {
    return setSearchValue(event.target.value);
  };

  const handleSearchReset = () => {
    setSearchValue('');
  };

  const [searchFilteredItems, setSearchFilteredItems] = useState<any>([]);

  useEffect(() => {
    if (debouncedSearchValue === '') {
      setSearchFilteredItems(sortedItems);
      return;
    }
    const filteredItems: any = [];
    const normalizedSearch = debouncedSearchValue.toLowerCase();
    const matchesSearch = (field: unknown) => {
      const comparable = extractComparableValue(field);
      if (comparable === null || comparable === undefined) return false;
      return String(comparable).toLowerCase().includes(normalizedSearch);
    };
    sortedItems.forEach((item: any) => {
      if (
        matchesSearch(item?.Ticker) ||
        matchesSearch(item?.Name) ||
        matchesSearch(item?.Asset) ||
        matchesSearch(item?.Strategy) ||
        matchesSearch(item?.Year) ||
        matchesSearch(item?.Index) ||
        matchesSearch(item?.['']) ||
        matchesSearch(item?.id)
      ) {
        filteredItems.push(item);
      }
    });
    setSearchFilteredItems(filteredItems);
  }, [sortedItems, debouncedSearchValue]);

  const exportRows = searchFilteredItems.length ? searchFilteredItems : data;

  const resolvedExportColumns = useMemo(() => {
    if (exportColumns?.length) {
      return exportColumns;
    }
    return buildTableExportColumnsFromRowKeys(exportRows);
  }, [exportColumns, exportRows]);

  const handleDownloadExcel = useCallback(() => {
    const sheet = buildTableExportSheet(exportRows, resolvedExportColumns, {
      includeParentHeaderRow: includeExportParentHeaderRow,
    });
    const ws = utils.aoa_to_sheet(sheet.aoa);
    if (sheet.merges?.length) {
      ws['!merges'] = sheet.merges;
    }
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Data');
    writeFileXLSX(wb, `${title}.xlsx`);
  }, [exportRows, includeExportParentHeaderRow, resolvedExportColumns, title]);

  const handleDownloadCSV = useCallback(() => {
    downloadTableExportCsv(exportRows, resolvedExportColumns, title, {
      includeParentHeaderRow: includeExportParentHeaderRow,
    });
  }, [exportRows, includeExportParentHeaderRow, resolvedExportColumns, title]);

  const handleOptionClick = useCallback(
    (name: string) => {
      switch (name) {
        case 'View in full screen':
          makeFullscreen();
          setOpenMenu(false);
          break;

        case 'Download XLS':
          handleDownloadExcel();
          setOpenMenu(false);
          break;

        case 'Download CSV':
          handleDownloadCSV();
          setOpenMenu(false);
      }
    },
    [handleDownloadCSV, handleDownloadExcel, makeFullscreen],
  );

  const [hasScroll, setHasScroll] = useState(false);

  const [titleRows, setTitleRows] = useState(1);

  const titleTextbox = useRef(null);

  const adjustTitleHeight = useCallback(() => {
    if (titleTextbox.current) {
      const numRows = (titleTextbox.current as unknown as any).rows;
      setTitleRows(numRows);
      (titleTextbox.current as unknown as any).style.height = 'inherit';
      (titleTextbox.current as unknown as any).style.height =
        `${(titleTextbox.current as unknown as any).scrollHeight}px`;
    }
  }, []);

  useEffect(() => {
    setTimeout(() => adjustTitleHeight(), 50);
  }, [subtitle]);

  return (
    <Wrapper $gridheight={gridHeight} ref={ref}>
      <HeaderWrapper>
        <TitleWrapper>
          <Header height={titleRows * 20}>
            <TextArea ref={titleTextbox} defaultValue={title} readOnly />
          </Header>
          {subtitle && (
            <SubHeader $titlerows={titleRows}>
              <SubtitleTextArea defaultValue={subtitle} readOnly />
            </SubHeader>
          )}
        </TitleWrapper>
        <RightWrapper $issearchvisible={isSearchVisible ? 1 : 0}>
          {isSearchVisible && (
            <SearchWrapper>
              <TableSearchBox
                value={searchValue}
                onValueChange={handleSearchChange}
                onValueReset={handleSearchReset}
                placeholder="Search"
              />
            </SearchWrapper>
          )}
          <MenuIconWrapper onClick={handleMenuClick}>
            <MenuIcon />
          </MenuIconWrapper>
        </RightWrapper>
      </HeaderWrapper>
      {openMenu && (
        <MenuOptions onMouseLeave={() => setOpenMenu(false)}>
          <Options>
            {MenuOptionsData.map(({ id, name }: { id: number; name: string }) => {
              return (
                <Items key={id} onClick={() => handleOptionClick(name)}>
                  {name}
                </Items>
              );
            })}
          </Options>
        </MenuOptions>
      )}
      {hasHeaders && (
        <BaseTable
          size={size}
          data={[]}
          dataColors={dataColors}
          gridHeight={gridHeight}
          headerHeight={isDoubleHeaderHeight ? headerHeight * 1.7 : headerHeight}
          sortBy={sort?.sortBy || 'Trend'}
          defaultSortBy={defaultSortBy}
          sortDirection={sort?.sortDirection}
          handleSetSort={handleSetSort}
          rowCount={searchFilteredItems.length}
          rowGetter={({ index }: { index: number }) => searchFilteredItems[index]}
          rowHeight={rowHeight}
          onRowClick={onRowClick}
          dataKeys={headersDataKeys}
          dataKeysDescription={headersDataKeysDescription}
          dataKeysWidth={headersDataKeysWidth}
          dataKeysColors={dataKeysColors}
          dataTypes={headersDataTypes}
          disableSort={disableSort}
          hasRowNodes={hasRowNodes}
          firstItems={headerFirstItems}
          lastItems={headerLastItems}
          debouncedSearchValue={debouncedSearchValue}
          onHeaderClick={onHeaderClick}
          isLoading={isLoading}
          emptyStateText={emptyStateText}
          hasHeaders={hasHeaders}
          isDoubleHeaderHeight={isDoubleHeaderHeight}
          isDoubleSubHeaderHeight={isDoubleSubHeaderHeight}
          omitHeaders={omitHeaders}
          hasScroll={hasScroll}
          indexTitleWidth={indexTitleWidth}
          hasSubtitle={!!subtitle}
          columnIds={headerColumnIds}
        />
      )}
      <BaseTable
        isSubHeader={true}
        size={size}
        data={searchFilteredItems}
        dataColors={dataColors}
        gridHeight={gridHeight}
        headerHeight={isDoubleSubHeaderHeight ? headerHeight * 1.7 : headerHeight}
        sortBy={sort?.sortBy || 'Trend'}
        defaultSortBy={defaultSortBy}
        sortDirection={sort?.sortDirection}
        handleSetSort={handleSetSort}
        rowCount={searchFilteredItems.length}
        rowGetter={({ index }: { index: number }) => searchFilteredItems[index]}
        rowHeight={rowHeight}
        onRowClick={onRowClick}
        dataKeys={dataKeys}
        dataKeysDescription={dataKeysDescription}
        dataKeysWidth={dataKeysWidth}
        dataKeysColors={dataKeysColors}
        dataTypes={dataTypes}
        dataAlignments={dataAlignments}
        dataMidPoints={dataMidPoints}
        disableSort={disableSort}
        hasRowNodes={hasRowNodes}
        firstItems={firstItems}
        lastItems={lastItems}
        debouncedSearchValue={debouncedSearchValue}
        onHeaderClick={onHeaderClick}
        isLoading={isLoading}
        emptyStateText={emptyStateText}
        hasHeaders={hasHeaders}
        isDoubleHeaderHeight={isDoubleHeaderHeight}
        isDoubleSubHeaderHeight={isDoubleSubHeaderHeight}
        setHasScroll={setHasScroll}
        hasScroll={hasScroll}
        indexTitle={indexTitle}
        indexTitleWidth={indexTitleWidth}
        hasSubtitle={!!subtitle}
        columnIds={columnIds}
      />
    </Wrapper>
  );
}
